import {Injectable} from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import {ColumnFormatsDict} from "./table-utils.service";
import {KeyMap} from "../_typing/generic-types";
import {formatNumber, thousandsSeparate, upperFirst, removeUnderscore, stringDate} from "../lib/utils";
import {DateTimePeriod} from "../_typing/date-time-period";

(pdfMake as any).vfs = pdfFonts as any;

export interface ComponentEventsTablePdfOptions {
    docTitle: string;
    columns: string[];
    data: any[];
    selectedColsDict: any;
    attributeCols: string[];
    cpDict: any;
    ctDict: any;
    genericConstants: any;
    componentComponentDict: any;

    tileTitle?: string;
    dateRange?: { start: any; end: any };

    pageOrientation?: 'portrait' | 'landscape';
    maxColsPerPage?: number;
    minColsPerPage?: number;

    formats?: KeyMap<ColumnFormatsDict>;

    headerLeftImage?: string;   // data URL
    headerRightImage?: string;  // data URL
    createdBy?: string;
    createdAt?: Date;
}

// A4 page dimensions in points (72 points = 1 inch)
const PAGE_DIMENSIONS = {
    portrait: {width: 595.28, height: 841.89},
    landscape: {width: 841.89, height: 595.28}
};

@Injectable({providedIn: 'root'})
export class PdfExportService {
    maxAutoColumnsPortrait = 8;
    maxAutoColumnsLandscape = 12;

    constructor() {
    }

    private rajdhaniLoaded = false;

    /**
     * Inserts zero-width spaces into long words to allow line breaking.
     * This prevents long strings without natural break points from overflowing columns.
     */
    private insertSoftBreaks(text: string, maxWordLength: number = 20): string {
        if (!text || typeof text !== 'string') {
            return text;
        }
        // Split by whitespace, process each word, rejoin
        return text.split(/(\s+)/).map(segment => {
            // If it's whitespace, keep it as-is
            if (/^\s+$/.test(segment)) {
                return segment;
            }
            // If word is longer than threshold, insert zero-width spaces
            if (segment.length > maxWordLength) {
                let result = '';
                for (let i = 0; i < segment.length; i++) {
                    result += segment[i];
                    // Insert zero-width space every maxWordLength characters (but not at the end)
                    if ((i + 1) % maxWordLength === 0 && i < segment.length - 1) {
                        result += '\u200B'; // Zero-width space
                    }
                }
                return result;
            }
            return segment;
        }).join('');
    }

    // Call this before createPdf
    private async ensureRajdhaniFonts(): Promise<void> {
        if (this.rajdhaniLoaded) {
            return;
        }

        // Paths to your TTF files in assets
        const mediumUrl = 'assets/fonts/Rajdhani/Medium/Rajdhani-Medium.ttf';
        const boldUrl = 'assets/fonts/Rajdhani/Bold/Rajdhani-Bold.ttf';

        const [mediumBase64, boldBase64] = await Promise.all([
            this.loadFontBase64(mediumUrl),
            this.loadFontBase64(boldUrl),
        ]);

        const vfs = (pdfMake as any).vfs || {};

        vfs['Rajdhani-Medium.ttf'] = mediumBase64;
        vfs['Rajdhani-Bold.ttf'] = boldBase64;

        (pdfMake as any).vfs = vfs;

        const fonts = (pdfMake as any).fonts || {};
        fonts['Rajdhani'] = {
            normal: 'Rajdhani-Medium.ttf',
            bold: 'Rajdhani-Bold.ttf',
            italics: 'Rajdhani-Medium.ttf',
            bolditalics: 'Rajdhani-Bold.ttf',
        };
        (pdfMake as any).fonts = fonts;

        this.rajdhaniLoaded = true;
    }

    private async loadFontBase64(url: string): Promise<string> {
        const res = await fetch(url);
        if (!res.ok) {
            throw new Error(`Failed to load font: ${url} (${res.status})`);
        }
        const blob = await res.blob();

        return await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const dataUrl = reader.result as string; // "data:font/ttf;base64,AAAA..."
                const base64 = dataUrl.split(',')[1];    // strip the "data:...," prefix
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    async exportComponentEventsTableToPdf(opts: ComponentEventsTablePdfOptions, dtp: DateTimePeriod): Promise<void> {
        await this.ensureRajdhaniFonts();   // make sure Rajdhani is ready
        const {
            docTitle,
            columns,
            data,
            selectedColsDict,
            attributeCols,
            cpDict,
            ctDict,
            genericConstants,
            componentComponentDict,
            formats,
            tileTitle,
            pageOrientation = 'landscape',
            maxColsPerPage = 8,
            headerLeftImage,
            headerRightImage,
            createdBy,
            createdAt,
        } = opts;

        const H_MARGIN = 20;

        // Calculate available width for table content
        const pageDims = PAGE_DIMENSIONS[pageOrientation];
        const availableTableWidth = pageDims.width - (2 * H_MARGIN);

        const pdfColumns = columns.filter(c =>
            !['Select', 'Expand', 'expandedDetail'].includes(c)
        );
        const stickyColId = pdfColumns[0];
        const stickyCols = [stickyColId];
        const scrollCols = pdfColumns.filter(c => !stickyCols.includes(c));

        const MAX_COLS_PER_PAGE = maxColsPerPage - stickyCols.length;
        const columnChunks: string[][] = [];
        for (let i = 0; i < scrollCols.length; i += MAX_COLS_PER_PAGE) {
            const chunk = scrollCols.slice(i, i + MAX_COLS_PER_PAGE);
            // prepend sticky column(s) to each chunk
            columnChunks.push([...stickyCols, ...chunk]);
        }

        const tablesForChunks: any[] = columnChunks.map((chunkCols, chunkIndex) => {
            const headerRow = chunkCols.map(col => {
                const title = this.getPdfColumnTitle(col, selectedColsDict, attributeCols, cpDict, ctDict);
                return this.insertSoftBreaks(title);
            });

            const body: any[][] = [];
            // header row with background colour
            body.push(headerRow.map(text => ({
                text,
                style: 'tableHeader',
            })));

            (data || []).forEach((component: any) => {
                const row = chunkCols.map(col => {
                    let text = this.getPdfCellValue(
                        component,
                        col,
                        selectedColsDict,
                        attributeCols,
                        genericConstants,
                        componentComponentDict,
                        formats
                    );
                    // Ensure Date objects are formatted using mine time, not browser locale
                    if (text instanceof Date) {
                        text = stringDate(text);
                    }
                    // Apply soft breaks to allow long strings to wrap
                    text = this.insertSoftBreaks(text);
                    const selected = selectedColsDict?.[col];
                    const isNumericConstant =
                        selected?.type === 'constant_property' &&
                        genericConstants?.[component.id]?.[col]?.data_type === 'float';
                    let cell: any = {text, style: isNumericConstant ? 'numericCell' : 'tableCell'};
                    const fmt = formats[col]?.[component.id];
                    cell = this.applyFormatStyleToCell(cell, fmt, 8);
                    return cell;
                });
                body.push(row);
            });
            const table: any = {
                headerRows: 1,
                body,
            };

            // Calculate explicit column widths to ensure table fits within page bounds.
            // Must account for padding (4+4=8 per cell) and borders (~1 per cell) which pdfMake adds on top of content width.
            const colCount = chunkCols.length;
            const paddingPerCell = 8; // paddingLeft(4) + paddingRight(4)
            const borderPerCell = 2;  // left + right borders
            const totalOverhead = colCount * (paddingPerCell + borderPerCell);
            const availableContentWidth = availableTableWidth - totalOverhead;
            const colWidth = Math.max(availableContentWidth / colCount, 20); // minimum 20pt to prevent negative/tiny widths
            table.widths = chunkCols.map(() => colWidth);
            const tableContent: any = {
                ...(chunkIndex === 0 ? {} : {pageBreak: 'before'}),
                table,
                layout: {
                    hLineWidth: (i: number, node: any) =>
                        (i === 0 || i === node.table.body.length) ? 1 : 0.5,
                    vLineWidth: (i: number, node: any) =>
                        (i === 0 || i === node.table.widths?.length) ? 1 : 0.5,
                    hLineColor: () => '#999999',
                    vLineColor: () => '#999999',
                    paddingLeft: () => 4,
                    paddingRight: () => 4,
                    paddingTop: () => 2,
                    paddingBottom: () => 2,
                },
                margin: [0, 10, 0, 0],
            };

            return tableContent;
        });

        const productionPeriod = this.getProductionPeriodText(dtp);

        const docDefinition: any = {
            info: {title: docTitle},
            pageSize: 'A4',
            pageOrientation,
            pageMargins: [H_MARGIN, 60, H_MARGIN, 40],

            header: () => {
                const columns: any[] = [];

                // Left column (logo or empty)
                if (headerLeftImage) {
                    columns.push({
                        width: 'auto',
                        image: headerLeftImage,
                        fit: [60, 60],
                        margin: [0, 10, 10, 0],
                    });
                } else {
                    columns.push({
                        width: 'auto',
                        text: '',
                        fit: [60, 60],
                        margin: [0, 0, 10, 0],
                    });
                }

                // Center stack: title + production period
                columns.push({
                    width: '*',
                    stack: [
                        {
                            text: tileTitle || docTitle,
                            style: 'headerTitle',
                            alignment: 'center',
                            margin: [0, 5, 0, 2],
                        },
                        {
                            text: productionPeriod,
                            style: 'headerSubTitle',
                            alignment: 'center',
                            margin: [0, 0, 0, 5],
                        },
                    ],
                });

                // Right column (logo or empty)
                if (headerRightImage) {
                    columns.push({
                        width: 'auto',
                        image: headerRightImage,
                        fit: [60, 60],
                        alignment: 'right',
                        margin: [0, 10, 0, 0]
                    });
                } else {
                    columns.push({
                        width: 'auto',
                        text: '',
                        alignment: 'right',
                    });
                }

                return {
                    margin: [H_MARGIN, 15, H_MARGIN, 0],
                    columns,
                };
            },

            footer: (currentPage: number, pageCount: number) => ({
                margin: [H_MARGIN, 20, H_MARGIN, 20],
                columns: [
                    {
                        text: this.getCreatedByFooterText(createdBy, createdAt),
                        style: 'footerLeft',
                        alignment: 'left',
                    },
                    {
                        text: `Page ${currentPage} of ${pageCount}`,
                        style: 'footerRight',
                        alignment: 'right',
                    },
                ],
            }),

            content: [
                // ...
                ...tablesForChunks,
            ],

            styles: {
                headerTitle: {
                    fontSize: 14,
                    bold: true,
                    font: 'Rajdhani',
                },
                headerSubTitle: {
                    fontSize: 9,
                    color: '#666666',
                    font: 'Rajdhani',
                },
                footerLeft: {
                    fontSize: 8,
                    color: '#555555',
                    font: 'Rajdhani',
                },
                tableHeader: {
                    bold: true,
                    fontSize: 8,
                    fillColor: '#ffffff',  // header background
                    color: '#000000',      // header text colour
                    margin: [4, 2, 4, 2],
                    font: 'Rajdhani',
                    alignment: 'center',
                },
                tableCell: {
                    fontSize: 8,
                    color: '#3a3a3c',
                    margin: [4, 1, 4, 1],
                    font: 'Rajdhani',
                },
                numericCell: {
                    fontSize: 8,
                    color: '#3a3a3c',
                    margin: [4, 1, 4, 1],
                    font: 'Rajdhani',
                    alignment: 'right',
                },
            },

            defaultStyle: {
                fontSize: 8,
                font: 'Rajdhani',
            },
        };

        pdfMake.createPdf(docDefinition).download(`${docTitle}.pdf`);
    }

    // ---------- helpers ----------

    private getPdfColumnTitle(
        column: string,
        selectedColsDict: any,
        attributeCols: string[],
        cpDict: any,
        ctDict: any,
    ): string {
        const selected = selectedColsDict?.[column];
        const title = selected?.title;

        if (column === 'name') {
            return selected?.title ?? 'Name';
        }

        if (attributeCols.includes(column) && !selected?.related_type_id) {
            return title ? title : removeUnderscore(upperFirst(selected?.id ?? column));
        }

        if (selected?.type === 'constant_property') {
            const cp = cpDict?.[column];
            return title ? title : removeUnderscore(upperFirst(cp?.attributes?.alias ?? cp?.attributes?.name ?? column));
        }

        if (selected?.type === 'component_type') {
            const ct = ctDict?.[column];
            return title ? title : removeUnderscore(upperFirst(ct?.attributes?.name ?? column));
        }
        return title ? title : removeUnderscore(upperFirst(column));
    }

    private getPdfCellValue(
        component: any,
        column: string,
        selectedColsDict: any,
        attributeCols: string[],
        genericConstants: any,
        componentComponentDict: any,
        formats: KeyMap<ColumnFormatsDict>
    ): any {
        const selected = selectedColsDict?.[column];

        if (column === 'name') {
            return component?.attributes?.name ?? '';
        }

        // Handle linked component attributes (e.g., BAG_LOT:name)
        if (selected?.related_type_id && selected?.type === 'attribute') {
            const relatedTypeId = selected.related_type_id;
            const attributeId = selected.id; // e.g., 'name'
            const isDateAttribute = attributeId === 'start_time' || attributeId === 'end_time';
            const linkedComponents = componentComponentDict?.[component.id]?.[relatedTypeId];

            if (Array.isArray(linkedComponents) && linkedComponents.length > 0) {
                return linkedComponents
                    .map((c: any) => {
                        const val = c?.attributes?.[attributeId] ?? '';
                        return isDateAttribute && val ? stringDate(val) : val;
                    })
                    .filter(Boolean)
                    .join(', ');
            }
            const val = linkedComponents?.attributes?.[attributeId] ?? '';
            return isDateAttribute && val ? stringDate(val) : val;
        }

        if (attributeCols.includes(column) && !selected?.related_type_id) {
            if (column.includes('changed_by')) {
                return column === 'changed_by'
                    ? (component.attributes?.changed_by_name ?? '')
                    : (genericConstants?.[component.id]?.[column] ?? '');
            }

            if (column.includes('start_time') || column.includes('end_time')) {
                const value = column.includes('start_time')
                    ? component.attributes?.start_time
                    : component.attributes?.end_time;
                return value ? stringDate(value) : '';
            }

        }

        if (selected?.type === 'constant_property') {
            const gc = genericConstants?.[component.id]?.[column];
            if (gc && gc.data_type === 'float') {
                const decimals = formats[column]?.[component.id]?.decimals;
                return thousandsSeparate(formatNumber(gc.value, decimals, true))
            }
            if (gc && (gc.data_type === 'datetime' || gc.value instanceof Date)) {
                return gc?.value != null ? stringDate(gc.value) : '';
            }

            return gc?.value != null ? String(gc.value) : '';
        }

        if (selected?.type === 'component_type') {
            // First check if genericConstants has the names (for aggregation_type 'none')
            const gc = genericConstants?.[component.id]?.[column];
            if (gc && gc.data_type === 'list' && Array.isArray(gc.value)) {
                return gc.value
                    .filter(Boolean)
                    .map(v => v instanceof Date ? stringDate(v) : v)
                    .join(', ');
            }
            if (gc?.value != null) {
                return gc.value instanceof Date ? stringDate(gc.value) : String(gc.value);
            }

            // Fallback to componentComponentDict for other cases
            const rel = componentComponentDict?.[component.id]?.[column];
            if (Array.isArray(rel)) {
                return rel
                    .map((c: any) => c?.attributes?.name ?? c?.attributes?.alias ?? '')
                    .filter(Boolean)
                    .join(', ');
            }

            return rel?.attributes?.name ?? rel?.attributes?.alias ?? '';
        }

        return '';
    }

    private getProductionPeriodText(dtp: DateTimePeriod): string {
        if (!dtp) {
            return '';
        }
        const start = `${stringDate(dtp.start, {date_only: true})} at ${stringDate(dtp.start, {time_only: true})}`
        const end = `${stringDate(dtp.end, {date_only: true})} at ${stringDate(dtp.end, {time_only: true})}`

        return `Production period: from ${start} to ${end}`;
    }

    private getCreatedByFooterText(createdBy?: string, createdAt?: Date): string {
        if (!createdBy || !createdAt) {
            return '';
        }
        return `Created by ${createdBy} on ${stringDate(createdAt)}`;
    }

    async loadImageAsDataUrl(url: string, fallbackUrl?: string): Promise<string> {
        try {

            const response = await fetch(url, {credentials: 'include'})  // include cookies if needed
            if (!response.ok) throw new Error('Fetch failed');

            const blob = await response.blob();
            const validTypes = ['image/png', 'image/jpeg'];

            if (!validTypes.includes(blob.type)) {
                console.error(`Logo at ${url} is type ${blob.type} — unsupported by pdfMake. Using fallback.`);
                return this.loadImageAsDataUrl(fallbackUrl);
            }
            return new Promise(resolve => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
            });

        } catch (err) {
            console.error(`Failed to load logo from ${url} — using fallback.`);
            if (!fallbackUrl) {
                throw new Error('Both primary and fallback URL failed');
            }
            return this.loadImageAsDataUrl(fallbackUrl);

        }

    }

    private applyFormatStyleToCell(
        baseCell: any,
        format: any,
        baseFontSize: number = 8,
    ): any {
        if (!format || !format.style) {
            return baseCell;
        }

        const style = format.style || {};
        const cell = {...baseCell};

        // font-weight -> bold
        if (style['font-weight'] === 'bold') {
            cell.bold = true;
        }

        // font-style -> italics
        if (style['font-style'] === 'italic') {
            cell.italics = true;
        }

        // text-decoration -> decoration
        if (style['text-decoration'] === 'underline') {
            cell.decoration = 'underline';
        }
        if (style['text-decoration'] === 'line-through') {
            cell.decoration = 'lineThrough';
        }
        if (style['color']) {
            cell.color = style['color']
        }
        if (style['background-color']) {
            cell.fillColor = style['background-color'];
        }
        if (style['text-align']) {
            cell.alignment = style['text-align'];
        }
        return cell;
    }

    async loadImageFromDomAsPngDataUrl(url: string, fallbackUrl: string): Promise<string> {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';  // safe default; no-op on same-origin

            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.naturalWidth || img.width;
                    canvas.height = img.naturalHeight || img.height;

                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        throw new Error('No 2D context');
                    }

                    ctx.drawImage(img, 0, 0);

                    // Now we have a true PNG dataURL regardless of original Content-Type
                    const dataUrl = canvas.toDataURL('image/png');
                    resolve(dataUrl);
                } catch (e) {
                    console.warn('Error converting logo via canvas, using fallback.', e);
                    this.loadImageAsDataUrl(fallbackUrl).then(resolve);
                }
            };

            img.onerror = () => {
                console.warn('Error loading logo image, using fallback.');
                this.loadImageAsDataUrl(fallbackUrl).then(resolve);
            };

            img.src = url;
        });
    }
}
